import { useState } from 'react';
import useSWR from 'swr';
import { useDebounceValue } from 'usehooks-ts';
import Card from 'components/card';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';
import { formatTokenAmount } from 'utils/amountUtils';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import {
  EInvoicePaymentStatus,
  IInvoicePaymentListItem,
  INVOICE_STATUS_LABELS,
} from 'views/types/invoicePayments';
import { RequestDialog } from './components/RequestDialog';
import { statusPillClass } from './components/statusPill';

const PAGE_SIZE = 20;

const headerClass =
  'border-b border-gray-200 pb-2 pr-3 text-start text-xs font-bold uppercase text-gray-600 dark:border-white/20 dark:text-white';
const cellClass = 'py-2 pr-3 text-sm text-navy-700 dark:text-white';

// Launch assets are USDT/USDC (6 decimals) — display only; authoritative
// amounts render in fiat alongside.
const CRYPTO_DECIMALS: Record<string, number> = { USDT: 6, USDC: 6 };

export const InvoicePayments = () => {
  const { canRead } = usePermissions();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounce] = useDebounceValue(search, 700);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, mutate } = useSWR(
    ['/api/admin/invoice-payments', status, page, searchDebounce],
    () =>
      invoicePaymentsService.getList({
        page: page + 1,
        limit: PAGE_SIZE,
        status: status || undefined,
        search: searchDebounce || undefined,
      }),
    { refreshInterval: 15000 }
  );

  const rows: IInvoicePaymentListItem[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <ReadPermissionGate
      resource={PERMISSION_RESOURCES.INVOICE_PAYMENTS}
      fallback={
        <div className="mt-5 text-center text-gray-600 dark:text-white">
          You don't have access to the Invoice Payments page.
        </div>
      }
    >
      <div className="mt-3">
        <Card extra="p-4">
          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <select
              className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
              value={status}
              onChange={e => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All statuses</option>
              {Object.values(EInvoicePaymentStatus).map(value => (
                <option key={value} value={value}>
                  {INVOICE_STATUS_LABELS[value]}
                </option>
              ))}
            </select>
            <input
              className="w-72 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
              placeholder="Search: IP number, email, invoice no."
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            {isLoading && (
              <span className="text-sm text-gray-500">Loading…</span>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={headerClass}>No.</th>
                  <th className={headerClass}>Created</th>
                  <th className={headerClass}>Client</th>
                  <th className={headerClass}>Beneficiary</th>
                  <th className={headerClass}>Invoice No.</th>
                  <th className={headerClass}>Amount</th>
                  <th className={headerClass}>You Paid</th>
                  <th className={headerClass}>Method</th>
                  <th className={headerClass}>Flags</th>
                  <th className={headerClass}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700"
                    onClick={() => {
                      if (canRead(PERMISSION_RESOURCES.INVOICE_INNER_PAGE)) {
                        setSelectedId(row.id);
                      }
                    }}
                  >
                    <td className={`${cellClass} font-mono`}>
                      {row.invoicePaymentNumber}
                    </td>
                    <td className={cellClass}>
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className={cellClass}>
                      <div>{row.clientName ?? '—'}</div>
                      <div className="text-xs text-gray-500">
                        {row.clientEmail}
                      </div>
                    </td>
                    <td className={cellClass}>
                      <div>{row.beneficiaryName ?? '—'}</div>
                      <div className="text-xs text-gray-500">
                        {row.beneficiaryCountry}
                      </div>
                    </td>
                    <td className={cellClass}>{row.invoiceNumber}</td>
                    <td className={cellClass}>
                      {row.fiatAmount} {row.fiatCurrency}
                    </td>
                    <td className={cellClass}>
                      {formatTokenAmount(
                        row.cryptoAmount,
                        CRYPTO_DECIMALS[row.cryptoAsset] ?? 6
                      )}{' '}
                      {row.cryptoAsset}
                    </td>
                    <td className={cellClass}>{row.rail}</td>
                    <td className={cellClass}>
                      {row.riskFlags?.length ? (
                        <span
                          className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          title={row.riskFlags.join(', ')}
                        >
                          {row.riskFlags.length}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={cellClass}>
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${statusPillClass(
                          row.status
                        )}`}
                      >
                        {INVOICE_STATUS_LABELS[row.status] ?? row.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td
                      className="py-4 text-center text-sm text-gray-500"
                      colSpan={10}
                    >
                      No invoice payment requests.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-white">
              {total} request(s) · page {page + 1} / {pageCount}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
              >
                Prev
              </button>
              <button
                disabled={page + 1 >= pageCount}
                onClick={() => setPage(p => p + 1)}
                className="rounded border border-gray-200 px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
              >
                Next
              </button>
            </div>
          </div>
        </Card>
      </div>

      {selectedId && (
        <RequestDialog
          requestId={selectedId}
          isOpen={!!selectedId}
          onClose={() => setSelectedId(null)}
          refetchList={mutate}
        />
      )}
    </ReadPermissionGate>
  );
};

export default InvoicePayments;
